import { useMemo } from 'react'
import { fetchCruiseOfferMemberPricing } from 'actions/CruiseActions'
import useApiQuery from 'hooks/apiQuery/useApiQuery'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsEnabledLuxPlusDiscountedCruise } from 'luxPlus/selectors/featureToggle'

export type CruiseMemberPricing = {
  hasMemberPrice: boolean
  canSeeMemberPrice: boolean
  departureMemberPricing?: App.Cruises.CruiseOfferMemberPricing['departures']
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory>
}

export function useCruiseMemberPricing(
  offer?: App.CruiseOffer,
  departure?: App.CruiseDepartureDetails,
): CruiseMemberPricing {
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isLuxPlusDiscountedCruiseEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedCruise)

  const canSeeMemberPrice = Boolean(canRedeemLuxPlusBenefits && isLuxPlusDiscountedCruiseEnabled)
  const hasMemberPrice = Boolean(departure?.hasMemberPrice) && isLuxPlusDiscountedCruiseEnabled

  const offerId = offer?.id

  const query = useApiQuery({
    fieldName: 'memberPricing',
    getData: state => state.cruise.cruiseOfferMemberPricing[offerId!],
    getFetching: state => state.cruise.cruiseOfferMemberPricingLoading[offerId!],
    getError: state => state.cruise.cruiseOfferMemberPricingErrors[offerId!],
    getFetchAction: () => fetchCruiseOfferMemberPricing(offerId!),
    fetchDependencies: [offerId, canSeeMemberPrice, hasMemberPrice],
    enabled: !!offerId && canSeeMemberPrice && hasMemberPrice,
  })

  const departureMemberPricingByCategory = useMemo(() => {
    if (!departure || !canSeeMemberPrice || !hasMemberPrice) {
      return {}
    }

    const forDeparture = query.memberPricing?.departures.find((d) => d.id === departure.id)
    return forDeparture?.lowestPricesByCategory || {}
  }, [departure, query.memberPricing, canSeeMemberPrice, hasMemberPrice])

  return {
    hasMemberPrice,
    canSeeMemberPrice,
    departureMemberPricing: query.memberPricing?.departures,
    departureMemberPricingByCategory,
  }
}
